import { Flex, Stack } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { useEffect, useState } from "react"
import {
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
  ComposedChart,
  ReferenceArea,
  Label,
  DotProps,
} from "recharts"

import { ChangeDataPointOutput } from "@costory/types/endpoints/billy"
import {
  EventsResponses,
  getEventsFromCategory,
} from "@costory/types/endpoints/events"
import { ExploreResponses } from "@costory/types/endpoints/explorer"
import { Filters } from "@costory/types/filters"

import { PREVIOUS_CHART_COLOR } from "@costory/shared/const"
import {
  BUSINESS_METRIC_NAME,
  BUSINESS_METRIC_TECHNICAL_NAME,
  getFormattedTrendData,
  getTrendDisplayText,
  metricLineConfig,
  prepareChartData,
  tickFormatter,
} from "@costory/shared/utils/charts"
import {
  getConsistentChartColor,
  hexToRgba,
} from "@costory/shared/utils/consistentColors"
import {
  computeComparisonPeriodDates,
  isGroupByOptional,
} from "@costory/shared/utils/filters"
import { formatNumber } from "@costory/shared/utils/format"

import { SmartLegend } from "@costory/front/components/SmartLegend"
import {
  ChartTooltip,
  EVENTS_NAME,
} from "@costory/front/components/charts/ChartTooltip"
import { ForecastWarningAlert } from "@costory/front/components/charts/ForecastWarningAlert"
import { useLegendHover } from "@costory/front/components/charts/useLegendHover"
import { RenderEventDot } from "@costory/front/components/common/RenderEventDot"
import { useChartColors } from "@costory/front/components/common/useChartColors"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { HandlerEvents } from "@costory/front/pages/Explorer/ExplorerMainChart"
import {
  useExplorerChangeDataPointQuery,
  useExplorerQuery,
} from "@costory/front/queries/explore"
import {
  mergeWithEvents,
  createHandleLegendClick,
  createIsForecastDate,
  splitForecastData,
  getPeriodsToDisplay,
} from "@costory/front/utils/charts"
import { useYAxisLabelConfig } from "@costory/front/utils/format"

type Props = {
  filters: Filters
  height?: number
  withLegend?: boolean
  withMetricsLine: boolean
  events?: EventsResponses.Aggregated
  handlerEvents?: HandlerEvents
  showEventPointer?: boolean
  comparisonData?: ExploreResponses.ExplorerData
  showComparisonWithLegend?: string
  setHideComparisonChart?: (hideComparisonChart: boolean) => void
  showTrends?: boolean
  filterEventsByChangeDataPointPeriods?: boolean
  showCrossAddEvent?: boolean
  includeForecast?: boolean
  showIntervalBars?: boolean
}

export const LineChart = ({
  filters,
  height,
  events,
  handlerEvents,
  withLegend = true,
  showEventPointer = false,
  withMetricsLine,
  comparisonData,
  showComparisonWithLegend,
  setHideComparisonChart,
  showTrends,
  filterEventsByChangeDataPointPeriods = false,
  showCrossAddEvent = false,
  includeForecast = false,
  showIntervalBars = false,
}: Props) => {
  const [hideMetricLine, { toggle: toggleMetricLine }] = useDisclosure(false)
  const [hideComparisonLine, { toggle: toggleComparisonLine }] =
    useDisclosure(false)
  const [hoverOnEvents, setHoverOnEvents] = useState(false)
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set())
  const { handleLegendHover, getOpacity, shouldShowDots } = useLegendHover()
  // Use regular explorer query when forecast is enabled, otherwise use changeDataPoint query
  const explorerQueryWithForecast = useExplorerQuery(
    filters,
    includeForecast, // Enable only when forecast is requested
    { includeForecast },
  )
  const explorerQueryChangeDataPoint = useExplorerChangeDataPointQuery(
    filters,
    showTrends,
  )
  const explorerQuery = includeForecast
    ? explorerQueryWithForecast
    : explorerQueryChangeDataPoint
  const { axisTextStyle } = useChartColors()

  const yAxisLabel = useYAxisLabelConfig(filters.metricId, filters.currency)
  const yAxisLabelProps = filters.metricId
    ? { ...yAxisLabel, offset: 0 }
    : undefined

  const groupByOptional = isGroupByOptional(filters.groupBy)
  const [tooltipVisible, setTooltipVisible] = useState(true)

  const previousWithColors = comparisonData?.stackedBarChartData.items.map(
    (elem) => ({
      ...elem,
      color: PREVIOUS_CHART_COLOR,
    }),
  )
  useEffect(() => {
    if (setHideComparisonChart) setHideComparisonChart(hideComparisonLine)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hideComparisonLine])

  const handleLegendClick = createHandleLegendClick(
    toggleMetricLine,
    toggleComparisonLine,
    setHiddenSeries,
  )
  const filterEventsByChangeDataPointPeriodsActive =
    filterEventsByChangeDataPointPeriods && filters.aggBy == "Day"
  return (
    <QueryWrapper query={explorerQuery}>
      {({ data }) => {
        const changeDataPointPeriods: ChangeDataPointOutput["periods"] =
          "changeDataPointPeriods" in data
            ? (data.changeDataPointPeriods as ChangeDataPointOutput["periods"])
            : []
        const periodsToDisplay = getPeriodsToDisplay(changeDataPointPeriods)
        const chartData = prepareChartData(data, comparisonData)
        const mergedEventsData = mergeWithEvents(
          chartData,
          events,
          filterEventsByChangeDataPointPeriodsActive
            ? periodsToDisplay
            : undefined,
        )

        const withColors = data.stackedBarChartData.items.map(
          (elem, index) => ({
            ...elem,
            color: getConsistentChartColor(index),
          }),
        )
        const legendPayload = [
          ...withColors.map((item) => ({
            value: item.name,
            color: item.color,
            dataKey: item.name,
          })),
          ...(includeForecast
            ? withColors.map((item) => ({
                value: `${item.name} (forecast)`,
                color: hexToRgba(item.color, 0.5),
                dataKey: `${item.name} (forecast)`,
              }))
            : []),
          ...(previousWithColors?.map((item) => ({
            value: `${item.name} (previous)`,
            color: item.color,
            dataKey: `${item.name} (previous)`,
          })) ?? []),
          ...(withMetricsLine
            ? [
                {
                  value: BUSINESS_METRIC_NAME,
                  color: getConsistentChartColor(
                    data.stackedBarChartData.items.length,
                  ),
                  dataKey: BUSINESS_METRIC_TECHNICAL_NAME,
                },
                ...(includeForecast
                  ? [
                      {
                        value: `${BUSINESS_METRIC_NAME} (forecast)`,
                        color: hexToRgba(
                          getConsistentChartColor(
                            data.stackedBarChartData.items.length,
                          ),
                          0.5,
                        ),
                        dataKey: `${BUSINESS_METRIC_TECHNICAL_NAME} (forecast)`,
                      },
                    ]
                  : []),
              ]
            : []),
        ]
        const formattedChangeData = getFormattedTrendData(
          changeDataPointPeriods,
        )

        const isForecastDate = createIsForecastDate(
          includeForecast,
          filters.to,
          {
            aggBy: filters.aggBy as "Month" | "Week" | "Day" | undefined,
          },
        )
        const chartDataWithForecast = splitForecastData(
          mergedEventsData as Record<string, unknown>[],
          includeForecast,
          isForecastDate,
          withColors.map((item) => item.name),
          withMetricsLine ? BUSINESS_METRIC_TECHNICAL_NAME : undefined,
          true,
        )

        return (
          <Stack h="100%" gap="xs" style={{ minHeight: 0 }}>
            {includeForecast && data.forecastWarning && (
              <ForecastWarningAlert message={data.forecastWarning} />
            )}
            {withLegend && (!groupByOptional || !!showComparisonWithLegend) && (
              <SmartLegend
                payload={legendPayload}
                onEntryClick={handleLegendClick}
                showComparisonWithLegend={showComparisonWithLegend}
                hideMetricLine={hideMetricLine}
                hideComparison={hideComparisonLine}
                hiddenSeries={hiddenSeries}
                onHoverChange={handleLegendHover}
              />
            )}
            <Flex style={{ flex: 1, minHeight: 0 }}>
              <ResponsiveContainer width="100%" height={height ?? "100%"}>
                <ComposedChart
                  data={chartDataWithForecast}
                  stackOffset="sign"
                  style={axisTextStyle}
                  margin={{ right: withMetricsLine ? 15 : 75, left: 10 }}
                  onMouseMove={() => {
                    setTooltipVisible(true)
                  }}
                >
                  <CartesianGrid vertical={false} strokeDasharray="5 5" />
                  <XAxis
                    dataKey="agg_date"
                    angle={30}
                    height={80}
                    textAnchor="start"
                    fontSize={12}
                    tickFormatter={(val: string) =>
                      tickFormatter(val, filters.aggBy)
                    }
                    onClick={(event) => {
                      setHoverOnEvents(false)
                      if (handlerEvents) {
                        handlerEvents.createEventFromXAxisClick(
                          event as unknown as { value: string },
                        )
                      }
                    }}
                    tick={{
                      cursor: showEventPointer ? "copy" : undefined,
                    }}
                    style={axisTextStyle}
                    interval={
                      data.stackedBarChartData.arr.length > 15
                        ? Math.floor(data.stackedBarChartData.arr.length / 15)
                        : 0
                    }
                    padding={{
                      right: 10,
                    }}
                  />
                  <YAxis
                    label={yAxisLabelProps}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(value) =>
                      formatNumber({ value, currency: filters.currency })
                    }
                    fontSize={12}
                    style={axisTextStyle}
                    domain={[
                      (dataMin: number) =>
                        dataMin < 0 && Math.abs(dataMin) < 1 ? 0 : dataMin,
                      "auto",
                    ]}
                  />
                  {withMetricsLine && (
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      yAxisId={BUSINESS_METRIC_TECHNICAL_NAME}
                      orientation="right"
                      fontSize={12}
                      tickFormatter={(value) =>
                        formatNumber({ value, numberType: "decimal" })
                      }
                      style={axisTextStyle}
                      domain={[
                        (dataMin: number) =>
                          dataMin < 0 && Math.abs(dataMin) < 1 ? 0 : dataMin,
                        "auto",
                      ]}
                    />
                  )}
                  {/* Hidden Y-axis for Events */}
                  {events && (
                    <YAxis
                      yAxisId="events"
                      hide
                      domain={["dataMin", "dataMax"]}
                    />
                  )}
                  {showTrends &&
                    formattedChangeData?.map(
                      ({ startDate, endDate, color, type }) => {
                        const displayText = getTrendDisplayText(
                          type,
                          startDate,
                          endDate,
                        )
                        return (
                          <ReferenceArea
                            key={startDate}
                            x1={startDate}
                            x2={endDate}
                            fill={color}
                            label={
                              <Label position="inside" fill="white">
                                {displayText}
                              </Label>
                            }
                            onMouseEnter={() => setTooltipVisible(true)}
                            onMouseLeave={() => setTooltipVisible(false)}
                          />
                        )
                      },
                    )}
                  <Tooltip
                    cursor={tooltipVisible}
                    content={({ label, payload, coordinate, active }) => {
                      if (!tooltipVisible || !active) {
                        return null
                      }

                      return (
                        <ChartTooltip
                          filterEventsByChangeDataPointPeriods={
                            filterEventsByChangeDataPointPeriodsActive
                          }
                          label={label}
                          payload={
                            payload as {
                              name: string
                              value: number
                              dataKey: string
                              color: string
                            }[]
                          }
                          currency={filters.currency}
                          coordinate={{
                            x: coordinate?.x ?? 0,
                          }}
                          aggBy={filters.aggBy}
                          displayEvents={hoverOnEvents}
                          events={events && getEventsFromCategory(events)}
                          groupByOptional={groupByOptional}
                          previousDate={
                            payload?.[0]?.payload?.agg_date_previous
                          }
                          hideComparison={hideComparisonLine}
                          showIntervalBars={showIntervalBars}
                          isBarChart={false}
                        />
                      )
                    }}
                  />
                  {previousWithColors?.map((item) => (
                    <Line
                      key={`previous-${item.name}`}
                      dataKey={`${item.name} (previous)`}
                      stroke={item.color}
                      fill={item.color}
                      strokeWidth={1}
                      strokeDasharray="6 3"
                      connectNulls
                      hide={hideComparisonLine}
                      dot={
                        shouldShowDots(`${item.name} (previous)`)
                          ? { r: 2 }
                          : false
                      }
                      onMouseEnter={() => setHoverOnEvents(false)}
                      onMouseLeave={() => setHoverOnEvents(false)}
                      name={`${item.name} (previous)`}
                      strokeOpacity={
                        0.7 * getOpacity(`${item.name} (previous)`)
                      }
                    />
                  ))}
                  {withColors.slice(1).map((item) => (
                    <Line
                      key={item.name}
                      dataKey={item.name}
                      stroke={item.color}
                      fill={item.color}
                      strokeWidth={2}
                      connectNulls
                      hide={hiddenSeries.has(item.name)}
                      dot={false}
                      activeDot={{ r: 5 }}
                      onMouseEnter={() => setHoverOnEvents(false)}
                      onMouseLeave={() => setHoverOnEvents(false)}
                      strokeOpacity={getOpacity(item.name)}
                    />
                  ))}
                  {includeForecast &&
                    withColors
                      .slice(1)
                      .map((item) => (
                        <Line
                          key={`${item.name} (forecast)`}
                          dataKey={`${item.name} (forecast)`}
                          stroke={hexToRgba(item.color, 0.5)}
                          fill={hexToRgba(item.color, 0.5)}
                          strokeWidth={2}
                          strokeDasharray="4 4"
                          connectNulls
                          hide={hiddenSeries.has(`${item.name} (forecast)`)}
                          dot={false}
                          activeDot={{ r: 4 }}
                          onMouseEnter={() => setHoverOnEvents(false)}
                          onMouseLeave={() => setHoverOnEvents(false)}
                          strokeOpacity={getOpacity(`${item.name} (forecast)`)}
                        />
                      ))}
                  {withColors[0] && (
                    <Line
                      dataKey={withColors[0].name}
                      stroke={withColors[0].color}
                      fill={withColors[0].color}
                      strokeWidth={2}
                      connectNulls
                      dot={false}
                      activeDot={
                        showCrossAddEvent
                          ? (props: DotProps) => (
                              <ActiveDot
                                {...props}
                                onClick={() => {
                                  if (handlerEvents) {
                                    handlerEvents.createEventFromXAxisClick(
                                      {
                                        value:
                                          "payload" in props
                                            ? (
                                                props.payload as unknown as {
                                                  agg_date: string
                                                }
                                              ).agg_date
                                            : "",
                                      },
                                      true,
                                    )
                                  }
                                }}
                              />
                            )
                          : { r: 5 }
                      }
                      hide={hiddenSeries.has(withColors[0].name)}
                      onMouseEnter={() => setHoverOnEvents(true)}
                      onMouseLeave={() => setHoverOnEvents(false)}
                      strokeOpacity={getOpacity(withColors[0].name)}
                    />
                  )}
                  {includeForecast && withColors[0] && (
                    <Line
                      dataKey={`${withColors[0].name} (forecast)`}
                      stroke={hexToRgba(withColors[0].color, 0.5)}
                      fill={hexToRgba(withColors[0].color, 0.5)}
                      strokeWidth={2}
                      strokeDasharray="4 4"
                      connectNulls
                      dot={false}
                      activeDot={{ r: 4 }}
                      hide={hiddenSeries.has(
                        `${withColors[0].name} (forecast)`,
                      )}
                      onMouseEnter={() => setHoverOnEvents(true)}
                      onMouseLeave={() => setHoverOnEvents(false)}
                      strokeOpacity={getOpacity(
                        `${withColors[0].name} (forecast)`,
                      )}
                    />
                  )}
                  {withMetricsLine && (
                    <Line
                      stroke={getConsistentChartColor(
                        data.stackedBarChartData.items.length,
                      )}
                      fill={getConsistentChartColor(
                        data.stackedBarChartData.items.length,
                      )}
                      {...metricLineConfig}
                      dot={false}
                      activeDot={{ r: 5 }}
                      hide={hideMetricLine}
                      onMouseEnter={() => setHoverOnEvents(false)}
                      onMouseLeave={() => setHoverOnEvents(false)}
                      strokeOpacity={getOpacity(BUSINESS_METRIC_TECHNICAL_NAME)}
                    />
                  )}
                  {includeForecast && withMetricsLine && (
                    <Line
                      stroke={hexToRgba(
                        getConsistentChartColor(
                          data.stackedBarChartData.items.length,
                        ),
                        0.5,
                      )}
                      fill={hexToRgba(
                        getConsistentChartColor(
                          data.stackedBarChartData.items.length,
                        ),
                        0.5,
                      )}
                      dataKey={`${BUSINESS_METRIC_TECHNICAL_NAME} (forecast)`}
                      yAxisId={BUSINESS_METRIC_TECHNICAL_NAME}
                      strokeWidth={2}
                      strokeDasharray="4 4"
                      connectNulls
                      dot={false}
                      activeDot={{ r: 4 }}
                      hide={hideMetricLine}
                      onMouseEnter={() => setHoverOnEvents(false)}
                      onMouseLeave={() => setHoverOnEvents(false)}
                      strokeOpacity={getOpacity(
                        `${BUSINESS_METRIC_TECHNICAL_NAME} (forecast)`,
                      )}
                    />
                  )}

                  {events && (
                    <Scatter
                      dataKey={EVENTS_NAME}
                      yAxisId="events"
                      fill="var(--mantine-color-warning-0)"
                      shape={<RenderEventDot />}
                      onMouseEnter={() => setHoverOnEvents(true)}
                      onMouseLeave={() => setHoverOnEvents(false)}
                      onClick={(val) => {
                        if (events && handlerEvents) {
                          handlerEvents.eventClickOnChart(val)
                        }
                      }}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </Flex>
          </Stack>
        )
      }}
    </QueryWrapper>
  )
}

const ComparisonLineChart = (props: Props) => {
  const { filters } = props

  const shouldFetchComparison = !!filters.previousFrom && !!filters.previousTo

  const previous = useExplorerQuery(
    {
      ...filters,
      from: filters.previousFrom || filters.from,
      to: filters.previousTo || filters.to,
      ...(shouldFetchComparison
        ? computeComparisonPeriodDates(
            filters.previousFrom!,
            filters.previousTo!,
            filters.datePreset,
          )
        : {}),
    },
    shouldFetchComparison,
  )

  if (!shouldFetchComparison) {
    return <LineChart {...props} />
  }

  return (
    <QueryWrapper query={previous}>
      {({ data }) => <LineChart {...props} comparisonData={data} />}
    </QueryWrapper>
  )
}

export const GetLineChart = (props: Props) => {
  return props.showComparisonWithLegend ? (
    <ComparisonLineChart {...props} />
  ) : (
    <LineChart {...props} />
  )
}

const ActiveDot = ({ cx, cy, onClick }: DotProps & { onClick: () => void }) => {
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null

  return (
    <svg
      onClick={onClick}
      x={(cx ?? 0) - 10}
      y={(cy ?? 0) - 10}
      width={20}
      height={20}
      fill="white"
      viewBox="0 0 24 24"
    >
      <circle
        cx="12"
        cy="12"
        r="10"
        fill="white"
        stroke="black"
        strokeWidth="2"
      />
      <path
        d="M12 6v12M6 12h12"
        stroke="black"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  )
}
